import { useEffect, useState, useCallback } from 'react'
import { useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import { Outlet, NavLink, useLocation, useSearchParams } from 'react-router-dom'

import { EditableComponent } from '../components'
import { ROLE_MANAGER, ROLE_FACILITATOR } from '../constants'
import { apolloAuthData, apolloEditStatusData } from '../services/apollo'

const GET_AGENDA_PAGES = gql`
  query AgendaPages {
    agendaPages {
      edges {
        node {
          databaseId
          postId
          title
          overview
          instruction
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS = gql`
  query GetAgendaItems {
    agendaItems(first: 50) {
      edges {
        node {
          order
          title
          lock
        }
      }
    }
  }
`

const Agenda = () => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  const isFacilitator = auth.user?.capabilities?.includes(ROLE_FACILITATOR)
  let location = useLocation()
  let [searchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const editStatus = useReactiveVar(apolloEditStatusData)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [getAgendaPages] = useLazyQuery(GET_AGENDA_PAGES, {
    fetchPolicy: 'no-cache',
  })
  const [getAgendaItems] = useLazyQuery(GET_AGENDA_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [agendaPage, setAgendaPage] = useState(null)
  const [agendaItems, setAgendaItems] = useState([])

  const loadData = useCallback(async () => {
    setIsDataLoading(true)

    Promise.all([getAgendaPages(), getAgendaItems()])
      .then((responses) => {
        // Agenda page
        const agendaPages =
          responses[0]?.data?.agendaPages?.edges?.map((edge) => edge.node) ?? []
        const _agendaPage = agendaPages[0] ?? null
        setAgendaPage(_agendaPage)

        // Agenda items
        let agendaItems =
          responses[1]?.data?.agendaItems?.edges?.map((edge) => edge.node) ?? []
        setAgendaItems(agendaItems.sort((a, b) => a.order - b.order))
      })
      .catch((err) => {
        console.error('Unable to load data', err)
      })
      .finally(() => {
        setIsDataLoading(false)
      })
  }, [getAgendaPages, getAgendaItems])

  // Load data every time the edit button is toggled
  useEffect(() => {
    if (isManager) {
      loadData()
    }
  }, [isManager, isEditOn, loadData])

  // Reload data when publish is successful
  useEffect(() => {
    if (!isEditOn) {
      return
    }

    if (editStatus.published === true) {
      loadData()
    }
  }, [editStatus.published, isEditOn, loadData])

  /**
   * Reload agenda items whenever the page is visited
   */
  useEffect(() => {
    if (location.pathname.startsWith('/agenda')) {
      loadData()
    }
  }, [location.pathname, loadData])

  return (
    <main className="min-h-[calc(100vh-200px)] overflow-hidden">
      <div className="container mb-[120px] max-w-screen-content pt-14 sm:px-0">
        <div className="relative pb-[10px]">
          <div className="absolute -right-[150px] top-0 -z-[1] h-[366px] w-[248px] bg-none bg-contain bg-right-top bg-no-repeat opacity-20 sm:bg-agenda-background lg:-right-[160px]"></div>
          {isDataLoading ? (
            <LoadingSkeleton />
          ) : !agendaPage ? (
            <p className="text-center">No data</p>
          ) : (
            <>
              <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    doc={agendaPage?.title ?? '<div></div>'}
                    toolbarOption={{
                      bold: false,
                      listNumber: false,
                      listBullet: false,
                    }}
                    tag={{
                      page: 'AGENDA_PAGE',
                      type: 'title',
                      databaseId: agendaPage?.databaseId,
                      action: 'update',
                    }}
                    disableParagraph={true}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: agendaPage?.title ?? '<div></div>',
                    }}
                  />
                )}
              </h1>
              {isEditOn ? (
                <EditableComponent
                  editable={isEditOn}
                  doc={agendaPage?.overview ?? '<div></div>'}
                  tag={{
                    page: 'AGENDA_PAGE',
                    type: 'overview',
                    databaseId: agendaPage?.databaseId,
                    action: 'update',
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: agendaPage?.overview ?? '<div></div>',
                  }}
                />
              )}
              <div className="my-14">
                {isEditOn ? (
                  <div className="mb-6 text-center">
                    <EditableComponent
                      editable={isEditOn}
                      doc={agendaPage?.instruction ?? '<div></div>'}
                      tag={{
                        page: 'AGENDA_PAGE',
                        type: 'instruction',
                        databaseId: agendaPage?.databaseId,
                        action: 'update',
                      }}
                    />
                  </div>
                ) : (
                  <p
                    className="mb-6 text-center"
                    dangerouslySetInnerHTML={{
                      __html: agendaPage?.instruction ?? '<div></div>',
                    }}
                  />
                )}
                <ul className="nostyle flex flex-wrap items-center justify-center gap-3 md:flex-nowrap">
                  {agendaItems.map((day) => (
                    <li key={day.title}>
                      {isManager || isFacilitator ? (
                        <NavLink
                          to={day.title.replace(/ /, '').toLowerCase()}
                          preventScrollReset={true}
                          state={{ title: day.title }}
                          className={({ isActive }) =>
                            `'hover:bg-skin-button-accent hover:text-white' block rounded-lg px-6 py-4 font-bold leading-none shadow-sm transition-colors ${
                              isActive
                                ? 'bg-skin-button-accent text-white'
                                : 'bg-bright-gray text-black-olive'
                            } active:translate-y-[0.75px] active:scale-[0.99] ${
                              isEditOn
                                ? 'pointer-events-none cursor-not-allowed opacity-50'
                                : ''
                            }`
                          }
                        >
                          {day.title}
                        </NavLink>
                      ) : day.lock ? (
                        <span className="pointer-events-none block cursor-not-allowed rounded-lg bg-bright-gray px-6 py-4 font-bold leading-none text-black-olive opacity-50 shadow-sm transition-colors">
                          {day.title}
                        </span>
                      ) : (
                        <NavLink
                          to={day.title.replace(/ /, '').toLowerCase()}
                          preventScrollReset={true}
                          state={{ title: day.title }}
                          className={({ isActive }) =>
                            `block rounded-lg px-6 py-4 font-bold leading-none shadow-sm transition-colors hover:bg-skin-button-accent
                              hover:text-white ${
                                isActive
                                  ? 'bg-skin-button-accent text-white'
                                  : 'bg-bright-gray text-black-olive'
                              } active:translate-y-[0.75px] active:scale-[0.99]`
                          }
                        >
                          {day.title}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <Outlet />
        </div>
      </div>
    </main>
  )
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse space-y-2.5">
      {/** Title */}
      <div className="mb-10 flex w-full items-center justify-center space-x-2">
        <div className="mb-[28px] mt-[28px] h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Overview */}
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[400px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[440px] items-center space-x-2">
        <div className="h-2.5 w-32 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
      <div className="flex w-full max-w-[400] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="mb-10 flex w-full max-w-[360px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      {/** Select a day to continue... */}
      <div className="!mt-20 flex w-full items-center justify-center space-x-2">
        <div className="mb-6 h-2.5 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
      {/** Agenda items */}
      <div className="!mb-20 flex w-full items-center justify-center space-x-2">
        <div className="h-[48px] w-[100px] rounded-lg bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-[48px] w-[100px] rounded-lg bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-[48px] w-[100px] rounded-lg bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-[48px] w-[100px] rounded-lg bg-gray-300 dark:bg-gray-700"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default Agenda
